import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { buildPublicUnitSyntheticKey, type PublicUnitForMenu } from './types';
import { useRevealedSection } from '../../../services/revealedPrices';
import { FULL_VERSION_CAPTION, GATED_DISABLED_ACCENT_SX } from '../FullVersionTooltip';

/**
 * Public-side floor-plan viewer modal. Visual structure and zoom controls
 * mirror the agent's `floorPlanOpen` Dialog in `SuperPickerApp.tsx`
 * (~line 24206). Sensitive numeric prices are NOT shown — only the
 * already-masked `priceDisplay` / `psfDisplay` strings the backend emitted.
 */

interface FloorPlanModalProps {
  open: boolean;
  unit: PublicUnitForMenu | null;
  onClose: () => void;
  /**
   * Compare-cart state + handlers. Optional so the modal still works in
   * isolation (e.g. tests) without a parent compare cart wired in.
   */
  isInCompare?: boolean;
  compareCount?: number;
  compareCapacity?: number;
  onToggleCompare?: (unit: PublicUnitForMenu) => void;
  onOpenCompare?: () => void;
}

const formatArea = (sqft: number | null): string => {
  if (sqft == null || !Number.isFinite(sqft)) return '-';
  return `${Math.round(sqft).toLocaleString()} sqft`;
};

export const FloorPlanModal: React.FC<FloorPlanModalProps> = ({
  open,
  unit,
  onClose,
  isInCompare = false,
  compareCount = 0,
  compareCapacity = 3,
  onToggleCompare,
  onOpenCompare,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Honour per-row reveals: if this unit's prices have been unmasked
  // elsewhere (e.g. via the unit-actions menu), swap the masked strings
  // for the real numeric values both in the on-screen summary and the
  // exported PDF. Falls back to whatever `unit.priceDisplay` /
  // `psfDisplay` already carries when no reveal has been paid for.
  const revealedUnits = useRevealedSection('towerView', unit?.projectId ?? null);
  const revealedRow = unit
    ? revealedUnits?.[buildPublicUnitSyntheticKey(unit.blockNumber, unit.unitNumber)]
    : undefined;
  const formatRevealedDollar = (value: number | null | undefined): string | null => {
    if (value == null || !Number.isFinite(value) || value <= 0) return null;
    return `$${Math.round(value).toLocaleString('en-SG')}`;
  };
  const effectivePriceDisplay =
    formatRevealedDollar(revealedRow?.price) ?? unit?.priceDisplay ?? null;
  const effectivePsfDisplay =
    formatRevealedDollar(revealedRow?.psf) ?? unit?.psfDisplay ?? null;

  const compareFull = compareCount >= compareCapacity && !isInCompare;
  const compareLabel = isInCompare
    ? 'Remove from Compare'
    : compareFull
      ? `Compare Full (${compareCount}/${compareCapacity})`
      : `Add to Compare (${compareCount}/${compareCapacity})`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100dvh' : '90vh',
          bgcolor: 'var(--color-surface)',
          color: 'rgba(var(--color-text-rgb), 0.9)',
          boxShadow: isMobile ? 'none' : '0 4px 24px rgba(var(--color-shadow-rgb), 0.4)',
          border: '1px solid rgba(var(--color-text-rgb), 0.08)',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: 'var(--color-text-strong)',
          fontWeight: 600,
          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
          pb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ color: 'inherit', pr: 1 }}>
            Floor Plan - {unit?.projectName ?? ''} {unit?.unitNumber ?? ''}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(var(--color-text-rgb), 0.9)' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: isMobile ? 1.5 : 2,
          overflow: 'auto',
          maxHeight: isMobile ? 'none' : '80vh',
          bgcolor: 'var(--color-surface)',
          borderColor: 'rgba(var(--color-text-rgb), 0.08)',
        }}
      >
        {!unit?.floorplan ? (
          <Box sx={{ minHeight: isMobile ? '45vh' : '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
              No floor plan available for this unit.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0, width: '100%', display: 'flex', justifyContent: 'center' }}>
              <TransformWrapper
                initialScale={1}
                minScale={0.8}
                maxScale={5}
                centerOnInit
                limitToBounds={false}
                centerZoomedOut
                wheel={{ step: 0.08 }}
                pinch={{ step: 5 }}
                doubleClick={{ step: 0.8 }}
                panning={{ disabled: false, velocityDisabled: true }}
              >
                {({ zoomIn, zoomOut, resetTransform }) => (
                  <Box sx={{ width: '100%', position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        display: 'flex',
                        gap: 0.5,
                        p: 0.5,
                        borderRadius: 1,
                        bgcolor: 'rgba(var(--color-shadow-rgb), 0.45)',
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      <IconButton size="small" onClick={() => zoomOut()} sx={{ color: 'var(--color-text-strong)' }}>
                        <ZoomOutIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => zoomIn()} sx={{ color: 'var(--color-text-strong)' }}>
                        <ZoomInIcon fontSize="small" />
                      </IconButton>
                      <Button size="small" onClick={() => resetTransform()} sx={{ minWidth: 0, px: 1, color: 'var(--color-text-strong)' }}>
                        Reset
                      </Button>
                    </Box>
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: isMobile ? '45vh' : '70vh',
                        overflow: 'hidden',
                        borderRadius: '8px',
                      }}
                      contentStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        component="img"
                        src={unit.floorplan ?? undefined}
                        alt={`Floor plan for ${unit.projectName} ${unit.unitNumber}`}
                        sx={{
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: 1,
                          boxShadow: 2,
                        }}
                      />
                    </TransformComponent>
                  </Box>
                )}
              </TransformWrapper>
            </Box>

            <Box
              sx={{
                width: { xs: '100%', sm: 280 },
                minWidth: { xs: 0, sm: 240 },
                borderLeft: { xs: 'none', sm: '1px solid rgba(var(--color-text-rgb), 0.12)' },
                borderTop: { xs: '1px solid rgba(var(--color-text-rgb), 0.12)', sm: 'none' },
                pl: { xs: 0, sm: 2 },
                pt: { xs: 2, sm: 0 },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Unit Info
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 0.6, columnGap: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>Status</Typography>
                <Typography variant="caption">{unit.unitStatus ?? '-'}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>Project</Typography>
                <Typography variant="caption">{unit.projectName}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>Unit</Typography>
                <Typography variant="caption">{unit.unitNumber}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>Block</Typography>
                <Typography variant="caption">{unit.blockNumber ?? '-'}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>Bedroom</Typography>
                <Typography variant="caption">{unit.bedroom ?? '-'}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>Bathroom</Typography>
                <Typography variant="caption">{unit.bathroom ?? '-'}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>Area</Typography>
                <Typography variant="caption">{formatArea(unit.areaSqft)}</Typography>
                {effectivePriceDisplay && (
                  <>
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>Price</Typography>
                    <Typography variant="caption">{effectivePriceDisplay}</Typography>
                  </>
                )}
                {effectivePsfDisplay && (
                  <>
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>PSF</Typography>
                    <Typography variant="caption">{effectivePsfDisplay}</Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)',
          bgcolor: 'var(--color-surface)',
          px: isMobile ? 1.5 : 2,
          py: isMobile ? 1.5 : 1,
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: isMobile ? 'stretch' : 'flex-end',
        }}
      >
        <Button
          onClick={onClose}
          sx={{ color: 'rgba(var(--color-text-rgb), 0.9)', width: isMobile ? '100%' : 'auto' }}
        >
          Close
        </Button>
        {unit?.floorplan && (
          <>
            <Button
              variant="outlined"
              disabled
              sx={{
                ...GATED_DISABLED_ACCENT_SX,
                flexDirection: 'column',
                gap: 0.25,
                py: 1,
                borderRadius: 1.5,
                width: isMobile ? '100%' : 'auto',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <PictureAsPdfIcon fontSize="small" />
                <Typography component="span" variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  Export PDF
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  lineHeight: 1.2,
                  color: 'rgba(var(--color-text-rgb), 0.65)',
                }}
              >
                {FULL_VERSION_CAPTION}
              </Typography>
            </Button>
            {onToggleCompare && (
              <Button
                variant="outlined"
                onClick={() => onToggleCompare(unit)}
                disabled={compareFull}
                sx={{
                  borderColor: 'rgba(var(--color-text-rgb), 0.5)',
                  color: 'rgba(var(--color-text-rgb), 0.9)',
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                {compareLabel}
              </Button>
            )}
            {onOpenCompare && (
              <Button
                variant="contained"
                onClick={onOpenCompare}
                disabled={compareCount === 0}
                sx={{ width: isMobile ? '100%' : 'auto' }}
              >
                View Compare Cart
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FloorPlanModal;
